import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, Button, Typography, useMediaQuery } from '@material-ui/core'
import { ReasonGenerateValidationCode, ValidationCodeChannel } from '@mutual-services/account-types'
import { useApi } from '~/store/store'
import { CodeInput, Bold } from '~/components'
import { mutualTheme } from '~/utils/theme'
import { Edit2Icon, MailIcon } from '~/assets/icons'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { CashedDataPayload, AccountPendenciesCachedData, MODAL_TYPE } from '../..'
import { OnboardingRegisterDataActions, trackEvent } from '~/service/analytics'

export interface ValidateInfoModalProps {
  email?: string,
  phone?: string,
  seconds: number,
  isEmail?: boolean,
  canResend: boolean,
  cachedData: AccountPendenciesCachedData,
  restart: (newExpiryTimestamp: number) => void,
  setCanResend: Dispatch<SetStateAction<boolean>>,
  setOpenedModalType: (modalType: MODAL_TYPE) => void,
  modifyCachedData: (key: keyof AccountPendenciesCachedData, payload: CashedDataPayload) => void
}

const FIELD_COUNT = 4
const RESEND_TIME_MS = 10000

export const ValidateInfoModal: React.FC<ValidateInfoModalProps> = (props) => {
  const {
    isEmail, seconds, restart, setOpenedModalType,
    canResend, setCanResend, modifyCachedData, cachedData
  } = props
  const { email, phone } = cachedData
  const [code, setCode] = useState('')
  const [hasSentBefore, setHasSentBefore] = useState(false)
  const { _id: accountId } = useAuthAccount()

  const [validateCode, validateCodeState, clearRequestState] = useApi('validateCode')
  const [, generateValidationCodeState] = useApi('generateValidationCode')
  const [sendRenewedValidationCode, sendRenewedValidationCodeState] = useApi('sendRenewedValidationCode')

  const channel = useMemo(() => isEmail ? ValidationCodeChannel.EMAIL : ValidationCodeChannel.SMS, [isEmail])
  const formatedChannel = useMemo(() => channel === ValidationCodeChannel.EMAIL ? 'email' : 'celular', [channel])

  const isLoading = useMemo(() =>
    generateValidationCodeState.loading ||
    sendRenewedValidationCodeState.loading ||
    validateCodeState.loading, [generateValidationCodeState.loading, sendRenewedValidationCodeState.loading, validateCodeState.loading])

  const clearCode = useCallback(() => {
    setCode('')
  }, [setCode])

  const matches = useMediaQuery(mutualTheme.breakpoints.down('md'))

  const restartTimer = useCallback(() => {
    restart(new Date().getTime() + RESEND_TIME_MS)
  }, [restart])

  const sendCode = useCallback(async (channel: ValidationCodeChannel) => {
    restartTimer()
    setCanResend(false)

    const result = await sendRenewedValidationCode({
      channel,
      accountId,
      generateReason: isEmail ? ReasonGenerateValidationCode.REGISTER_EMAIL_VALIDATION : ReasonGenerateValidationCode.REGISTER_PHONE_VALIDATION
    })
    if (result.error) {
      toast.error(`Erro ao validar ${formatedChannel}`, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
        closeButton: false
      })
      clearCode()
      clearRequestState()
      return
    }
    setHasSentBefore(true)
  }, [isEmail, accountId, restartTimer, setCanResend, sendRenewedValidationCode, clearCode, clearRequestState, setHasSentBefore])

  const resendCode = useCallback(async (channel: ValidationCodeChannel) => {
    restartTimer()
    clearRequestState()
    setCanResend(false)

    await sendRenewedValidationCode({
      channel,
      accountId,
      generateReason: isEmail ? ReasonGenerateValidationCode.REGISTER_EMAIL_VALIDATION : ReasonGenerateValidationCode.REGISTER_PHONE_VALIDATION
    })

    setCanResend(true)
    clearCode()
  }, [clearRequestState, setCanResend, sendRenewedValidationCode, restartTimer, clearCode, isEmail, accountId])

  const submitCode = useCallback(async () => {
    const result = await validateCode({
      accountId,
      code: parseInt(code, 10)
    })

    if (result.ok) {
      modifyCachedData(isEmail ? 'emailValidated' : 'phoneValidated', true)
      trackEvent(isEmail
        ? OnboardingRegisterDataActions.EMAIL_VALIDATION
        : OnboardingRegisterDataActions.PHONE_VALIDATION
      )
      setOpenedModalType(isEmail ? MODAL_TYPE.SuccessValidationEmailModal : MODAL_TYPE.SuccessValidationPhoneModal)
      return
    }

    toast.error(`Erro ao validar ${formatedChannel}`, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      closeButton: false
    })
    clearCode()
    clearRequestState()
  }, [validateCode, code, clearRequestState, formatedChannel, clearCode])

  useEffect(() => {
    const isCodeValid = code.length === 4
    if (isCodeValid) {
      submitCode()
    }
  }, [code, submitCode])

  const onSendCodeClick = () => {
    if (!hasSentBefore) {
      sendCode(channel)
      return
    }
    resendCode(channel)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 640 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center', marginBottom: 4 }}>
        <Typography sx={{ marginBottom: 1, fontWeight: 'fontWeightBold' }} variant={matches ? 'airbnbH3' : 'airbnbH2'}>
          {isEmail ? 'Validar e-mail' : 'Validar telefone'}
        </Typography>
        <Typography variant={matches ? 'airbnbBody2' : 'airbnbBody1'}>
          Clique no botão <Bold>Receber código</Bold> para enviarmos o código no {isEmail ? 'e-mail' : 'telefone'}
          <Typography variant={matches ? 'airbnbBody2' : 'airbnbBody1'} sx={{ fontWeight: 'fontWeightBold' }}>
            {' ' + (isEmail ? email : phone)}
          </Typography>
        </Typography>
        <Typography variant={matches ? 'airbnbBody2' : 'airbnbBody1'} sx={{ display: 'inline', marginTop: { xs: 1 } }}>
          Insira o código de validação abaixo:
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', marginBottom: 4 }}>
        <CodeInput
          name='codeValidation'
          inputMode='numeric'
          fields={FIELD_COUNT}
          onChange={setCode}
          disabled={!hasSentBefore}
        />
      </Box>

      <Typography variant={matches ? 'airbnbBody2' : 'airbnbBody1'} sx={{ textAlign: 'center', color: 'textGray1.main' }}>
        {isEmail
          ? 'Não recebeu o código de validação? Clique no botão abaixo para reenviar ou verifique o e-mail informado e altere seus dados.'
          : 'Não recebeu o código de validação? Clique no botão abaixo para reenviar ou verifique o telefone informado e altere seus dados.'
        }
      </Typography>

      <Box sx={{
        display: 'flex',
        flexDirection: [
          'column-reverse',
          'column-reverse',
          'row',
          'row',
          'row'
        ],
        justifyContent: 'space-between',
        marginTop: 3
      }}>
        <Button
          variant='contained'
          color='info'
          size='large'
          onClick={() => {
            if (isEmail) {
              setOpenedModalType(MODAL_TYPE.UpdateEmailModal)
            } else {
              setOpenedModalType(MODAL_TYPE.UpdatePhoneModal)
            }
          }}
          startIcon={<Edit2Icon htmlColor='inherit' />}
          sx={{
            marginTop: [2, 2, 0, 0, 0],
            height: 56,
            fontFamily: 'Airbnb',
            fontWeight: 'fontWeightBold',
            color: 'secondary.main',
            ':hover': {
              bgcolor: 'rgba(54,141,247,0.2)'
            }
          }}
          disabled={isLoading || !canResend}
        >
          Alterar {isEmail ? 'e-mail' : 'telefone'}
        </Button>
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={onSendCodeClick}
          startIcon={<MailIcon htmlColor='inherit' />}
          sx={{
            height: 56,
            fontFamily: 'Airbnb',
            fontWeight: '700'
          }}
          disabled={!canResend || isLoading}
        >
          {canResend ? 'Receber código' : `Receber código (${seconds})`}
        </Button>
      </Box>
    </Box >
  )
}
